// src/views/pages/restaurant-reservations/WaitlistPage.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Layout, message, Spin } from "antd";
import axios from "axios";
import moment from "moment";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNotification } from "../../context/NotificationContext";

// Imported components
import HeaderBar from "./waitlist/components/HeaderBar";
import SidebarPanel from "./waitlist/components/SidebarPanel";
import QueueCard from "./waitlist/components/QueueCard";
import TablesGrid from "./waitlist/components/TablesGrid";
import DetailsDrawer from "./waitlist/components/DetailsDrawer";
import AddWalkInModal from "./waitlist/components/AddWalkInModal";
import ReassignModal from "./waitlist/components/ReassignModal";

const { Content, Sider } = Layout;
const API_URL = process.env.REACT_APP_API_URL;
const ITEM_TYPE = "WAITLIST_ITEM";

export default function WaitlistPage() {
  const { notify } = useNotification();

  // --- refs (stable + safe)
  const notifyRef = useRef(notify);
  useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const didInitRef = useRef(false);
  const reqSeqRef = useRef({ tables: 0, bookings: 0, waitlist: 0 });

  // interval safe (always uses latest refreshAll)
  const refreshFnRef = useRef(null);

  // -----------------------------
  // State
  // -----------------------------
  const [tables, setTables] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);

  const [offlineMode, setOfflineMode] = useState(false);
  const [search, setSearch] = useState("");

  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);

  const [notifyLog, setNotifyLog] = useState([]);
  const [auditLog, setAuditLog] = useState([]);

  const [tablesLoading, setTablesLoading] = useState(false);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const [alternatives, setAlternatives] = useState([]);
  const [tick, setTick] = useState(0);

// ✅ Selected guest for one-click seating in TablesGrid
const [selectedGuest, setSelectedGuest] = useState(null);


  // tick for “Actual wait”
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(i);
  }, []);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const safeNotify = useCallback((type, msg, opts = {}) => {
    notifyRef.current?.(type, msg);
    if (!opts.silent) {
      const fn = message[type] || message.info;
      fn(msg);
    }
  }, []);

  const logAudit = useCallback((who, action) => {
    setAuditLog((prev) => [...prev, { at: new Date().toISOString(), who, action }]);
  }, []);

  const logNotify = useCallback((msg, to) => {
    setNotifyLog((prev) => [{ at: new Date().toISOString(), msg, to }, ...prev]);
  }, []);

  const calcActualWaitMin = useCallback(
    (joinTime) => Math.max(moment().diff(joinTime, "minutes"), 0),
    []
  );

  const estimateWaitAI = useCallback(({ partySize, isWeekend, occupancyPct }) => {
    const base = 6 + Math.floor(Number(partySize || 2) * 2.2);
    const weekendBoost = isWeekend ? 1.2 : 1.0;
    const occBoost = 1 + Math.min(Math.max((occupancyPct - 45) / 100, 0), 0.8);
    return Math.max(5, Math.round(base * weekendBoost * occBoost));
  }, []);

  // -----------------------------
  // Derived
  // -----------------------------
  const locations = useMemo(
    () => Array.from(new Set((tables || []).map((t) => t.location).filter(Boolean))),
    [tables]
  );

  const occupancy = useMemo(() => {
    const total = tables.length;
    const occupied = tables.filter((t) => t.status === "occupied").length;
    const cleaning = tables.filter((t) => t.status === "cleaning").length;
    const available = total - occupied - cleaning;
    const pct = total ? Math.round((occupied / total) * 100) : 0;
    return { total, occupied, cleaning, available, pct };
  }, [tables]);

  const isWeekend = useMemo(() => moment().isoWeekday() >= 6, []);

  const waitStats = useMemo(() => {
    const active = waitlist.filter((w) => w.status !== "canceled" && w.status !== "seated");

    const waiting = active.filter((w) => w.status === "waiting").length;
    const notified = active.filter((w) => w.status === "notified").length;

    // ✅ avg from actual wait (not estimatedWait)
    const avgWait =
      active.length > 0
        ? Math.round(active.reduce((sum, w) => sum + calcActualWaitMin(w.joinTime), 0) / active.length)
        : 0;

    const overruns = active.filter((w) => {
      const eta = Number(w.estimatedWait || 0);
      const actual = calcActualWaitMin(w.joinTime);
      return eta > 0 && actual > eta;
    }).length;

    return { activeCount: active.length, waiting, notified, avgWait, overruns };
  }, [waitlist, tick, calcActualWaitMin]);

  const getTableById = useCallback(
    (id) => (tables || []).find((t) => String(t.id) === String(id)) || null,
    [tables]
  );

  // -----------------------------
  // API calls
  // -----------------------------
  const fetchTables = useCallback(async (params = {}) => {
    if (offlineMode) return;

    setTablesLoading(true);
    const seq = ++reqSeqRef.current.tables;

    try {
      const queryParams = new URLSearchParams(params).toString();
      const res = await axios.get(
        `${API_URL}/pos-module/tables${queryParams ? `?${queryParams}` : ""}`,
        { headers: authHeaders() }
      );

      const raw = res.data?.data ?? res.data ?? [];
      const rows = Array.isArray(raw) ? raw : Object.values(raw || {});

      const normalized = rows.map((table) => ({
        id: table.id,
        label: table.table_no || table.label || `Table ${table.id}`,
        location: table.location || table.table_location || "Main Hall",
        capacity: Number(table.seating_capacity || table.capacity || 2),
        status: String(table.current_status || table.status || "available").toLowerCase(),
      }));

      if (!mountedRef.current || seq !== reqSeqRef.current.tables) return;
      setTables(normalized);
    } catch (error) {
      safeNotify("error", "Failed to fetch tables");
    } finally {
      setTablesLoading(false);
    }
  }, [authHeaders, offlineMode, safeNotify]);

  const fetchBookings = useCallback(async (params = {}) => {
    if (offlineMode) return;

    setBookingsLoading(true);
    const seq = ++reqSeqRef.current.bookings;

    try {
      const date = moment().format("YYYY-MM-DD");
      const response = await axios.get(`${API_URL}/pos/reservations/listings`, {
        params: { reservation_date: date, ...params },
        headers: authHeaders(),
      });

      const raw = response.data?.data ?? response.data ?? [];
      const rows = Array.isArray(raw) ? raw : Object.values(raw || {});

      const normalized = rows.map((row) => ({
        id: row.id,
        name: row.guest_name ?? row.name ?? "",
        phone: row.contact_number ?? row.phone ?? row.mobile ?? "",
        email: row.email ?? null,
        partySize: row.party_size ?? row.partySize ?? 2,
        time: row.reservation_time ?? row.time ?? null,
        tableId: row.assigned_table_id ?? row.tableId ?? "unassigned",
        source: row.reservation_source ?? row.source ?? "Phone",
        status: row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : "Confirmed",
        notes: row.special_requests ?? row.notes ?? "",
        isVIP: false,
        fromBooking: true,
      }));

      if (!mountedRef.current || seq !== reqSeqRef.current.bookings) return;
      setBookings(normalized);
    } catch (error) {
      safeNotify("error", error.response?.data?.error || "Failed to fetch bookings");
    } finally {
      setBookingsLoading(false);
    }
  }, [authHeaders, offlineMode, safeNotify]);

  const fetchWaitlist = useCallback(async (params = {}) => {
    if (offlineMode) return;

    setWaitlistLoading(true);
    const seq = ++reqSeqRef.current.waitlist;

    try {
      const queryParams = new URLSearchParams(params).toString();
      const res = await axios.get(
        `${API_URL}/pos/waitlist${queryParams ? `?${queryParams}` : ""}`,
        { headers: authHeaders() }
      );

      const raw = res.data?.data ?? res.data ?? [];
      const rows = Array.isArray(raw) ? raw : Object.values(raw || {});

      const normalized = rows.map((row) => {
        const name = row.name ?? row.guest_name ?? row.customerName ?? row.customer_name ?? "";
        const phone = row.phone ?? row.contact_number ?? row.mobile ?? row.customerPhone ?? "";

        const datePart = String(row.join_date || row.joinDate || "").split("T")[0];
        const timePart = row.join_time || row.joinTime;

        // ✅ timezone-safe join time
        const joinTime =
          row.created_at ||
          row.createdAt ||
          (datePart && timePart
            ? moment(`${datePart} ${timePart}`, "YYYY-MM-DD HH:mm:ss").format()
            : new Date().toISOString());

        return {
          id: row.id,
          name: String(name).trim(),
          phone: String(phone).trim(),
          email: row.email ?? null,
          joinTime,
          partySize: row.partySize ?? row.party_size ?? 2,
          location: row.location ?? row.location_preference ?? row.table_location ?? null,
          tableId: row.tableId ?? row.assigned_table_id ?? null,
          status: String(row.status || "waiting").toLowerCase(),
          notes: row.notes ?? row.special_requests ?? null,
          source: row.source ?? "Walk-in",
          estimatedWait: row.estimatedWait ?? row.estimated_wait_minutes ?? row.estimated_wait ?? null,
          paidSkip: !!(row.paidSkip ?? row.paid_skip),
          isVIP: !!(row.isVIP ?? row.is_vip),
          fromBooking: !!(row.fromBooking ?? row.from_booking),
          bookingId: row.bookingId ?? row.booking_id ?? null,
        };
      });

      if (!mountedRef.current || seq !== reqSeqRef.current.waitlist) return;
      setWaitlist(normalized);
    } catch (e) {
      safeNotify("error", "Failed to fetch waitlist");
    } finally {
      setWaitlistLoading(false);
    }
  }, [authHeaders, offlineMode, safeNotify]);

  const refreshAll = useCallback(
    async ({ silent = true } = {}) => {
      await Promise.allSettled([fetchTables(), fetchBookings(), fetchWaitlist()]);
      if (!silent) safeNotify("success", "Data refreshed");
    },
    [fetchTables, fetchBookings, fetchWaitlist, safeNotify]
  );

  // set ref for interval
  useEffect(() => {
    refreshFnRef.current = refreshAll;
  }, [refreshAll]);

  // Auto-refresh every 30s using ref (prevents stale closure)
  useEffect(() => {
    const interval = setInterval(() => {
      refreshFnRef.current?.({ silent: true });
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Initial load ONCE
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    refreshAll({ silent: true });
  }, [refreshAll]);

  // -----------------------------
  // UI actions
  // -----------------------------
const openDetails = useCallback((item) => {
  setSelected(item);
  setDrawerOpen(true);

  // ✅ keep table-grid seating in sync
  if (item && item.type !== "table") setSelectedGuest(item);
}, []);


  const notifyGuest = useCallback(
    async (item) => {
      // TODO: connect to SMS/WhatsApp API
      logNotify("Table ready", item.phone);
      safeNotify("success", "Guest notified", { silent: false });
      logAudit("System", `Notified ${item.name}`);
    },
    [logNotify, safeNotify, logAudit]
  );

  const addToQueue = useCallback(
    async (booking) => {
      try {
        const values = {
          name: booking.name,
          phone: booking.phone,
          email: booking.email,
          party_size: booking.partySize,
          source: "Booking",
          from_booking: true,
          booking_id: booking.id,
        };
        await axios.post(`${API_URL}/pos/waitlist/add`, values, { headers: authHeaders() });
        safeNotify("success", "Added to queue", { silent: false });
        logAudit("Receptionist", `Added booking ${booking.id} to queue`);
        refreshAll({ silent: true });
      } catch (error) {
        safeNotify("error", "Failed to add to queue", { silent: false });
      }
    },
    [authHeaders, safeNotify, refreshAll, logAudit]
  );

  const seatNow = useCallback(
    async (item, tableId) => {
      try {
        await axios.put(
          `${API_URL}/pos/waitlist/edit/${item.id}`,
          { status: "seated", tableId },
          { headers: authHeaders() }
        );
        safeNotify("success", "Guest seated", { silent: false });
        logAudit("Receptionist", `Seated ${item.name} at table ${tableId}`);
        refreshAll({ silent: true });
      } catch (error) {
        safeNotify("error", "Failed to seat guest", { silent: false });
      }
    },
    [authHeaders, safeNotify, refreshAll, logAudit]
  );

  const updateWaitlistEntry = useCallback(
    async (id, updates) => {
      try {
        await axios.put(`${API_URL}/pos/waitlist/edit/${id}`, updates, { headers: authHeaders() });
        setWaitlist((prev) => prev.map((w) => (String(w.id) === String(id) ? { ...w, ...updates } : w)));
      } catch (error) {
        safeNotify("error", "Failed to update waitlist", { silent: false });
      }
    },
    [authHeaders, safeNotify]
  );

  const reassignTable = useCallback(
    async (item, tableId) => {
      try {
        await axios.put(`${API_URL}/pos/waitlist/edit/${item.id}`, { tableId }, { headers: authHeaders() });
        safeNotify("success", "Table reassigned", { silent: false });
        logAudit("Receptionist", `Reassigned ${item.name} to table ${tableId}`);
        refreshAll({ silent: true });
      } catch (error) {
        safeNotify("error", "Failed to reassign table", { silent: false });
      }
    },
    [authHeaders, safeNotify, refreshAll, logAudit]
  );

  // -----------------------------
  // Search filtering
  // -----------------------------
  const matchingBookings = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return [];
    return bookings.filter((b) => {
      const name = String(b.name || "").toLowerCase();
      const phone = String(b.phone || "").toLowerCase();
      const id = String(b.id || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || id.includes(q);
    });
  }, [bookings, search]);

  const filteredWaitlist = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return waitlist;
    return waitlist.filter((w) => {
      const name = String(w.name || "").toLowerCase();
      const phone = String(w.phone || "").toLowerCase();
      const id = String(w.id || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || id.includes(q);
    });
  }, [waitlist, search]);

  const loading = tablesLoading || bookingsLoading || waitlistLoading;

  return (
    <div className="page-wrapper">
      <DndProvider backend={HTML5Backend}>
        <Layout style={{ minHeight: "100vh", background: "#f5f7fb", borderRadius: 8, overflow: "hidden" }}>
          <HeaderBar
            search={search}
            setSearch={setSearch}
            openAdd={() => setAddOpen(true)}
            refreshAll={() => refreshAll({ silent: false })}
            safeNotify={safeNotify}
            offlineMode={offlineMode}
            setOfflineMode={setOfflineMode}
          />

          <Layout>
            <Sider width={330} style={{ background: "transparent", padding: 14 }}>
              <SidebarPanel waitStats={waitStats} occupancy={occupancy} isWeekend={isWeekend} notifyLog={notifyLog} auditLog={auditLog} />
            </Sider>

            <Content style={{ padding: 14 }}>
              <Layout style={{ background: "transparent" }}>
                <Content style={{ paddingRight: 14 }}>
                  {loading ? <Spin tip="Loading data..." style={{ display: "block", margin: "20px auto" }} /> : null}

                  <QueueCard
                    search={search}
                    matchingBookings={matchingBookings}
                    onSelectBooking={openDetails}
                    getTableById={getTableById}
                    waitlist={filteredWaitlist}
                    openAdd={() => setAddOpen(true)}
                    openDetails={openDetails}
                    notifyGuest={notifyGuest}
                    addToQueue={addToQueue}
                    seatNow={seatNow}
                    itemType={ITEM_TYPE}
                      upcomingBookings={bookings}
                    onAddBookingToQueue={addToQueue}
                    onOpenBooking={openDetails}
                    estimateWaitAI={(partySize) => estimateWaitAI({ partySize, isWeekend, occupancyPct: occupancy.pct })}
                    WaitlistRowActions={(item) => ({
                      items: [
                        { label: "Edit", key: "edit", onClick: () => openDetails(item) },
                        {
                          label: "Cancel",
                          key: "cancel",
                          danger: true,
                          onClick: () => updateWaitlistEntry(item.id, { status: "canceled" }),
                        },
                      ],
                    })}
                  />
                </Content>

                <Sider width={510} style={{ background: "transparent" }}>
          <TablesGrid
  tables={tables}
  openDetails={openDetails}
  safeNotify={safeNotify}
  seatNow={seatNow}
  selectedGuest={selectedGuest}
  upcomingBookings={bookings}               // ✅ fix: use bookings you already have
  onClearSelectedGuest={() => setSelectedGuest(null)}
/>



                </Sider>
              </Layout>
            </Content>
          </Layout>

          <DetailsDrawer
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
            selected={selected}
            selectedTable={getTableById(selected?.tableId)}
            alternatives={alternatives}
            reassignTable={reassignTable}
            seatGuest={seatNow}
            notifyGuest={notifyGuest}
            setReassignOpen={setReassignOpen}
            offlineMode={offlineMode}
            safeNotify={safeNotify}
            updateWaitlistEntry={updateWaitlistEntry}
            updateBooking={() => {}}
             tables={tables} 
            logAudit={logAudit}
          />

          <AddWalkInModal
            open={addOpen}
            onClose={() => setAddOpen(false)}
            locations={locations}
            onSubmit={async (formValues) => {
              try {
                const payload = {
                  name: formValues.name,
                  phone: formValues.phone,
                  email: formValues.email || null,
                  party_size: formValues.partySize,
                  location: formValues.location || null,
                  notes: formValues.notes || null,
                  source: formValues.source || "Walk-in",
                  estimated_wait: formValues.estimatedWait || null,
                  paid_skip: formValues.paidSkip || false,
                  is_vip: formValues.isVIP || false,
                  from_booking: false,
                  booking_id: null,
                };

                await axios.post(`${API_URL}/pos/waitlist/add`, payload, { headers: authHeaders() });

                safeNotify("success", "Walk-in added", { silent: false });
                logAudit("Receptionist", `Added walk-in ${payload.name}`);
                setAddOpen(false);
                refreshAll({ silent: true });
              } catch (error) {
                safeNotify("error", error.response?.data?.error || "Failed to add walk-in", { silent: false });
              }
            }}
          />

          <ReassignModal
            reassignOpen={reassignOpen}
            setReassignOpen={setReassignOpen}
            selected={selected}
            selectedTable={getTableById(selected?.tableId)}
            locations={locations}
            alternatives={alternatives}
            fetchAvailableTables={async () => tables.filter((t) => t.status === "available")}
            reassignTable={reassignTable}
            safeNotify={safeNotify}
          />
        </Layout>
      </DndProvider>
    </div>
  );
}
